import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { Service } from "src/apis/services";
import NoDataAlert from "src/pages/components/NoDataAlert";
import { formatDateToBR } from "src/utils/date";
import TableActionsCell from "./TableActionsCell";

type CompanyServicesTableProps = {
  services: Service[];
  hideConfidentialFields?: boolean;
};

const CompanyServicesTable: FC<CompanyServicesTableProps> = ({
  services,
  hideConfidentialFields = true,
}): JSX.Element => {
  const hasServices = useMemo(() => Boolean(services) && services.length > 0, [services]);

  const [entries, setEntries] = useState(services);
  const [selectedService, setSelectedService] = useState<Service | null>(null);

  useEffect(() => {
    if (hasServices) {
      setEntries(services);
    }
  }, [hasServices, services]);

  const formatValue = (value?: string | null): string => {
    if (value === null || value === undefined) {
      return "-";
    }

    const normalizedValue = String(value).trim();

    if (!normalizedValue || normalizedValue.toLowerCase() === "null") {
      return "-";
    }

    return normalizedValue;
  };

  const renderDetailItem = (label: string, value?: string | number | boolean | null) => (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">
        {typeof value === "boolean"
          ? value ? "Sim" : "Não"
          : formatValue(value === undefined || value === null ? undefined : String(value))}
      </Typography>
    </Box>
  );

  return (
    <>
      {hasServices ? (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Data de atendimento</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Colaborador</strong></TableCell>
                <TableCell><strong>Psicólogo</strong></TableCell>
                <TableCell><strong>Empresa</strong></TableCell>
                <TableCell><strong>Cidade</strong></TableCell>
                {!hideConfidentialFields && (
                  <TableCell><strong>Observações</strong></TableCell>
                )}
                <TableCell><strong>Ações</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries?.map((service: Service) => (
                <TableRow
                  hover
                  key={service.id_atendimento}
                  onClick={() => setSelectedService(service)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>{formatDateToBR(service.atendimento)}</TableCell>
                  <TableCell>{formatValue(service.service_status || 'Realizado')}</TableCell>
                  <TableCell>{formatValue(service.colaborador)}</TableCell>
                  <TableCell>{formatValue(service.psicologo)}</TableCell>
                  <TableCell>{formatValue(service.empresa)}</TableCell>
                  <TableCell>{formatValue(service.cidade)}</TableCell>
                  {!hideConfidentialFields && (
                    <TableCell>{formatValue(service.observacoes)}</TableCell>
                  )}
                  <TableActionsCell
                    editUrl={`/atendimentos/${service.id_atendimento}/editar`}
                    showDelete={false}
                  />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <NoDataAlert />
      )}

      <Dialog
        open={Boolean(selectedService)}
        onClose={() => setSelectedService(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Dados do atendimento e colaborador</DialogTitle>
        <DialogContent>
          {selectedService && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                {renderDetailItem("ID do atendimento", selectedService.id_atendimento)}
                {renderDetailItem("Data de atendimento", formatDateToBR(selectedService.atendimento))}
                {renderDetailItem("Status", selectedService.service_status || "Realizado")}
              </Box>

              <Divider />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                {renderDetailItem("Colaborador", selectedService.colaborador)}
                {renderDetailItem("ID do colaborador", selectedService.id_colaborador || "-")}
                {renderDetailItem("Empresa", selectedService.empresa)}
                {renderDetailItem("ID da empresa", selectedService.id_empresa || "-")}
                {renderDetailItem("Área", selectedService.area)}
                {renderDetailItem("Setor", selectedService.setor)}
                {renderDetailItem("Cidade", selectedService.cidade)}
                {renderDetailItem("Psicólogo", selectedService.psicologo)}
              </Box>

              {!hideConfidentialFields && (
                <>
                  <Divider />
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {renderDetailItem("Queixa inicial", selectedService.queixa_inicial)}
                    {renderDetailItem("Observações", selectedService.observacoes)}
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedService(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CompanyServicesTable;
